import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import secureAxios from "../../utils/secureAxios";
import { getApiBaseURL } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";
import { formatPhone } from "../../utils/formatPhone";

const EditTenantPage = ({ tenant, onDone }) => {
  const tenant_id = tenant?.tenant_id;
  const navigate = useNavigate();
  const apiBase = getApiBaseURL();
  const { user } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    tenant_name: "",
    tenant_type: "customer",
    email: "", // Added email field
    phone_number: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_id: "",
    postal_code: "",
    is_active: false,
    emergency_contact_name: "", // Added for HIPAA
    emergency_contact_phone: "", // Added for HIPAA
  });

  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [accessLog, setAccessLog] = useState([]); // Track access history

  useEffect(() => {
    if (!tenant_id || tenant_id === "undefined") {
      console.error("⛔ Invalid or missing tenant_id");
      setError("Invalid tenant selected");
      return;
    }

    fetchTenantData();
    fetchStates();
    fetchAccessLog();
  }, [tenant_id]);

  const fetchTenantData = async () => {
    try {
      const response = await secureAxios.get(
        `${apiBase}/api/tenants/${tenant_id}`
      );
      const data = response.data;

      const tenantData = {
        tenant_name: data.tenant_name || "",
        tenant_type: data.tenant_type || "customer",
        email: data.email || "",
        phone_number: formatPhone(data.phone_number || ""),
        address_line_1: data.address_line_1 || "",
        address_line_2: data.address_line_2 || "",
        city: data.city || "",
        state_id: data.state_id || "",
        postal_code: data.postal_code || "",
        is_active: Boolean(data.is_active),
        emergency_contact_name: data.emergency_contact_name || "",
        emergency_contact_phone: formatPhone(
          data.emergency_contact_phone || ""
        ),
      };

      setFormData(tenantData);
      setOriginalData(tenantData); // Store for change detection
    } catch (err) {
      console.error("❌ Error fetching tenant info:", err);
      setError("Failed to load tenant information");
    }
  };

  const fetchStates = async () => {
    try {
      const response = await secureAxios.get(`${apiBase}/api/states`);
      setStates(response.data);
    } catch (err) {
      console.error("❌ Error loading states:", err);
    }
  };

  const fetchAccessLog = async () => {
    try {
      // Fetch recent access logs for this tenant
      const response = await secureAxios.get(
        `${apiBase}/api/audit/tenant/${tenant_id}/recent`
      );
      setAccessLog(response.data || []);
    } catch (err) {
      console.error("❌ Error loading access log:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue = value;
    if (name === "phone_number" || name === "emergency_contact_phone") {
      newValue = formatPhone(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : newValue,
    }));

    if (error) setError(null);
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const getChangedFields = () => {
    const changes = [];
    if (originalData) {
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== originalData[key]) {
          changes.push(key);
        }
      });
    }
    return changes;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Clean phone numbers for storage
      const cleanedPhone = formData.phone_number.replace(/\D/g, "");
      const cleanedEmergencyPhone = formData.emergency_contact_phone.replace(
        /\D/g,
        ""
      );

      const payload = {
        ...formData,
        phone_number: cleanedPhone,
        emergency_contact_phone: cleanedEmergencyPhone,
        tenant_name: formData.tenant_name.trim(),
        email: formData.email.trim().toLowerCase(),
        address_line_1: formData.address_line_1.trim(),
        address_line_2: formData.address_line_2.trim(),
        city: formData.city.trim(),
        postal_code: formData.postal_code.trim(),
        emergency_contact_name: formData.emergency_contact_name.trim(),
        updated_by: user.user_id, // Track who made the change
      };

      await secureAxios.put(`${apiBase}/api/tenants/${tenant_id}`, payload);

      // Log which fields were changed for audit purposes
      const changedFields = getChangedFields();
      console.log("✅ Tenant updated. Changed fields:", changedFields);

      alert("✅ Tenant updated successfully!");
      if (onDone) onDone();
    } catch (err) {
      console.error("❌ Failed to update tenant:", err);
      setError(err.response?.data?.error || "Failed to update tenant");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Edit Tenant</h3>
        {tenant?.is_primary && (
          <span className="badge bg-primary">Primary Tenant</span>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* PHI Access Warning */}
      <div className="alert alert-info mb-3" role="alert">
        <i className="bi bi-shield-lock"></i>{" "}
        <strong>Editing Protected Information</strong>
        <br />
        Tenant ID: <code>{tenant_id}</code>
        <br />
        All changes will be logged for HIPAA compliance.
        <br />
        Data retention: 7 years (2,555 days) per HIPAA requirements.
      </div>

      <form onSubmit={handleSubmit} className="row g-3">
        {/* Tenant Name */}
        <div className="col-md-6">
          <label className="form-label">
            Tenant Name <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            name="tenant_name"
            value={formData.tenant_name}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        {/* Tenant Type */}
        <div className="col-md-6">
          <label className="form-label">Tenant Type</label>
          <select
            className="form-select"
            name="tenant_type"
            value={formData.tenant_type}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="customer">Customer</option>
            <option value="vendor">Vendor</option>
            <option value="partner">Partner</option>
          </select>
        </div>

        {/* Email */}
        <div className="col-md-6">
          <label className="form-label">
            Email <span className="text-danger">*</span>
            <small className="text-muted ms-2">For HIPAA notifications</small>
          </label>
          <input
            className="form-control"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        {/* Phone Number */}
        <div className="col-md-6">
          <label className="form-label">Phone Number</label>
          <input
            className="form-control"
            type="tel"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            placeholder="(123) 456-7890"
            disabled={loading}
          />
        </div>

        {/* Address Section */}
        <div className="col-12">
          <h5 className="border-bottom pb-2">Address Information</h5>
        </div>

        {/* Address Line 1 */}
        <div className="col-12">
          <label className="form-label">Address Line 1</label>
          <input
            className="form-control"
            name="address_line_1"
            value={formData.address_line_1}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {/* Address Line 2 */}
        <div className="col-12">
          <label className="form-label">Address Line 2</label>
          <input
            className="form-control"
            name="address_line_2"
            value={formData.address_line_2}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {/* City */}
        <div className="col-md-4">
          <label className="form-label">City</label>
          <input
            className="form-control"
            name="city"
            value={formData.city}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {/* State */}
        <div className="col-md-4">
          <label className="form-label">State</label>
          <select
            className="form-select"
            name="state_id"
            value={String(formData.state_id || "")}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">Select State</option>
            {states.map((s) => (
              <option key={s.state_id} value={String(s.state_id)}>
                {s.state_code} - {s.state_name}
              </option>
            ))}
          </select>
        </div>

        {/* Postal Code */}
        <div className="col-md-4">
          <label className="form-label">ZIP Code</label>
          <input
            className="form-control"
            name="postal_code"
            value={formData.postal_code}
            onChange={handleChange}
            maxLength="10"
            disabled={loading}
          />
        </div>

        {/* Emergency Contact Section */}
        <div className="col-12">
          <h5 className="border-bottom pb-2">
            Emergency Contact
            <small className="text-muted ms-2">
              For HIPAA break-glass scenarios
            </small>
          </h5>
        </div>

        {/* Emergency Contact Name */}
        <div className="col-md-6">
          <label className="form-label">Emergency Contact Name</label>
          <input
            className="form-control"
            name="emergency_contact_name"
            value={formData.emergency_contact_name}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {/* Emergency Contact Phone */}
        <div className="col-md-6">
          <label className="form-label">Emergency Contact Phone</label>
          <input
            className="form-control"
            type="tel"
            name="emergency_contact_phone"
            value={formData.emergency_contact_phone}
            onChange={handleChange}
            placeholder="(123) 456-7890"
            disabled={loading}
          />
        </div>

        {/* Active Status */}
        <div className="col-12">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              id="is_active"
              disabled={loading || tenant?.is_primary}
            />
            <label className="form-check-label" htmlFor="is_active">
              <strong>Active</strong> - Tenant has system access
              {tenant?.is_primary && (
                <span className="text-danger ms-2">
                  (Primary tenant cannot be deactivated)
                </span>
              )}
            </label>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="col-12">
          <button
            type="submit"
            className="btn btn-primary me-2"
            disabled={loading || !hasChanges()}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Updating...
              </>
            ) : (
              <>
                <i className="bi bi-save"></i> Update Tenant
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onDone}
            disabled={loading}
          >
            Cancel
          </button>
          {hasChanges() && !loading && (
            <span className="text-muted ms-3">
              <i className="bi bi-info-circle"></i> You have unsaved changes
            </span>
          )}
        </div>
      </form>

      {/* Audit Trail Notice */}
      <div className="mt-3">
        <small className="text-muted">
          <i className="bi bi-clock-history"></i>
          {tenant?.created_at &&
            ` Created: ${new Date(tenant.created_at).toLocaleString()}`}
          {tenant?.updated_at && (
            <>
              <br />
              <i className="bi bi-pencil"></i> Last Updated:{" "}
              {new Date(tenant.updated_at).toLocaleString()}
            </>
          )}
        </small>
      </div>

      {/* Recent Access Log */}
      {accessLog.length > 0 && (
        <div className="mt-4">
          <h6 className="text-muted">
            <i className="bi bi-shield-check"></i> Recent Access Log
          </h6>
          <div className="small">
            {accessLog.slice(0, 5).map((log, idx) => (
              <div key={idx} className="mb-1">
                {new Date(log.timestamp).toLocaleString()} - {log.user_name} (
                {log.action})
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditTenantPage;
